/**
 * Version History Drawer Component
 */
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  Tag,
  Button,
  Typography,
  Space,
  Tooltip,
  Modal,
  message,
  Spin,
  Empty,
  Timeline,
  Upload,
  Input,
} from 'antd';
import {
  HistoryOutlined,
  DownloadOutlined,
  RollbackOutlined,
  DiffOutlined,
  DeleteOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import versionApi from '../services/versionApi';

dayjs.extend(relativeTime);

const { Text, Title } = Typography;
const { TextArea } = Input;

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function VersionHistoryDrawer({ visible, onClose, document, onVersionChange, onCompare }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [changeSummary, setChangeSummary] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState([]);

  useEffect(() => {
    if (visible && document?.id) {
      loadVersionHistory();
    }
  }, [visible, document?.id]);

  const loadVersionHistory = async () => {
    setLoading(true);
    try {
      const data = await versionApi.getVersionHistory(document.id);
      setVersions(data.versions || []);
    } catch (error) {
      console.error('Failed to load version history:', error);
      message.error('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (version) => {
    try {
      const response = await versionApi.downloadVersion(document.id, version.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', version.original_filename);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      message.error('Failed to download version');
    }
  };

  const handleRestore = async (version) => {
    Modal.confirm({
      title: 'Restore Version',
      content: (
        <div>
          <p>Are you sure you want to restore to Version {version.version_number}?</p>
          <p style={{ color: '#666' }}>
            This will make Version {version.version_number} the current version. 
            The current version will remain in history.
          </p>
        </div>
      ),
      okText: 'Restore',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setRestoring(version.id);
        try {
          await versionApi.restoreVersion(document.id, version.id);
          message.success(`Restored to Version ${version.version_number}`);
          loadVersionHistory();
          if (onVersionChange) {
            onVersionChange();
          }
        } catch (error) {
          console.error('Restore failed:', error);
          message.error('Failed to restore version');
        } finally {
          setRestoring(null);
        }
      },
    });
  };

  const handleDelete = async (version) => {
    if (version.is_current) {
      message.warning('Cannot delete the current version');
      return;
    }

    Modal.confirm({
      title: 'Delete Version',
      content: `Are you sure you want to delete Version ${version.version_number}? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await versionApi.deleteVersion(document.id, version.id);
          message.success(`Version ${version.version_number} deleted`);
          loadVersionHistory();
        } catch (error) {
          console.error('Delete failed:', error);
          message.error('Failed to delete version');
        }
      },
    });
  };

  const handleUploadNewVersion = async () => {
    if (!uploadFile) {
      message.warning('Please select a file to upload');
      return;
    }

    setUploading(true);
    try {
      await versionApi.createVersion(document.id, uploadFile, changeSummary);
      message.success('New version uploaded successfully');
      setShowUpload(false);
      setUploadFile(null);
      setChangeSummary('');
      loadVersionHistory();
      if (onVersionChange) {
        onVersionChange();
      }
    } catch (error) {
      console.error('Upload failed:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload new version';
      message.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleCompare = () => {
    if (selectedVersions.length !== 2) {
      message.warning('Please select exactly 2 versions to compare');
      return;
    }
    if (onCompare) {
      onCompare(document.id, selectedVersions[0], selectedVersions[1]);
    }
  };

  const toggleVersionSelection = (versionId) => {
    setSelectedVersions((prev) => {
      if (prev.includes(versionId)) {
        return prev.filter((id) => id !== versionId);
      }
      if (prev.length >= 2) {
        return [prev[1], versionId];
      }
      return [...prev, versionId];
    });
  };

  const getStatusIcon = (version) => {
    if (version.is_current) {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
    if (!version.is_processed) {
      return <ClockCircleOutlined style={{ color: '#faad14' }} />;
    }
    if (version.processing_error) {
      return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
    }
    return <HistoryOutlined style={{ color: '#1890ff' }} />;
  };

  return (
    <Drawer
      title={
        <Space>
          <HistoryOutlined />
          <span>Version History</span>
          {document && <Text type="secondary">- {document.original_filename}</Text>}
        </Space>
      }
      placement="right"
      width={480}
      onClose={onClose}
      open={visible}
      extra={
        <Space>
          <Button
            icon={<UploadOutlined />}
            onClick={() => setShowUpload(true)}
          >
            Upload New Version
          </Button>
        </Space>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px' }}>Loading version history...</p>
        </div>
      ) : versions.length === 0 ? (
        <Empty description="No version history available" />
      ) : (
        <>
          {versions.length >= 2 && (
            <div style={{ marginBottom: '16px' }}>
              <Button
                type="primary"
                icon={<DiffOutlined />}
                disabled={selectedVersions.length !== 2}
                onClick={handleCompare}
              >
                Compare Selected ({selectedVersions.length}/2)
              </Button>
              {selectedVersions.length > 0 && (
                <Button
                  style={{ marginLeft: '8px' }}
                  onClick={() => setSelectedVersions([])}
                >
                  Clear Selection
                </Button>
              )}
            </div>
          )}

          <Timeline
            items={versions.map((version) => ({
              dot: getStatusIcon(version),
              children: (
                <div
                  key={version.id}
                  style={{
                    padding: '12px',
                    background: selectedVersions.includes(version.id) ? '#e6f7ff' : '#fafafa',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    border: selectedVersions.includes(version.id) ? '1px solid #1890ff' : '1px solid #f0f0f0',
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleVersionSelection(version.id)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <Space>
                        <Text strong>Version {version.version_number}</Text>
                        {version.is_current && (
                          <Tag color="green">Current</Tag>
                        )}
                        {!version.is_processed && (
                          <Tag color="orange">Processing</Tag>
                        )}
                      </Space>
                      <div style={{ marginTop: '4px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {version.created_at
                            ? dayjs(version.created_at).format('MMM D, YYYY h:mm A')
                            : 'Unknown date'}
                        </Text>
                      </div>
                      <div style={{ marginTop: '4px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {formatFileSize(version.file_size_bytes)} | {version.chunk_count || 0} chunks
                        </Text>
                      </div>
                      {version.change_summary && (
                        <div style={{ marginTop: '8px' }}>
                          <Text style={{ fontSize: '13px', fontStyle: 'italic' }}>
                            "{version.change_summary}"
                          </Text>
                        </div>
                      )}
                    </div>
                    <Space direction="vertical" size="small">
                      <Space>
                        <Tooltip title="Download">
                          <Button
                            type="text"
                            icon={<DownloadOutlined />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(version);
                            }}
                          />
                        </Tooltip>
                        {!version.is_current && (
                          <>
                            <Tooltip title="Restore this version">
                              <Button
                                type="text"
                                icon={<RollbackOutlined />}
                                loading={restoring === version.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRestore(version);
                                }}
                              />
                            </Tooltip>
                            <Tooltip title="Delete version">
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(version);
                                }}
                              />
                            </Tooltip>
                          </>
                        )}
                      </Space>
                    </Space>
                  </div>
                </div>
              ),
            }))}
          />
        </>
      )}

      <Modal
        title="Upload New Version"
        open={showUpload}
        onOk={handleUploadNewVersion}
        onCancel={() => {
          setShowUpload(false);
          setUploadFile(null);
          setChangeSummary('');
        }}
        confirmLoading={uploading}
        okText="Upload"
      >
        <div style={{ marginBottom: '16px' }}>
          <Upload
            beforeUpload={(file) => {
              setUploadFile(file);
              return false;
            }}
            maxCount={1}
            onRemove={() => setUploadFile(null)}
            fileList={uploadFile ? [uploadFile] : []}
          >
            <Button icon={<UploadOutlined />}>Select File</Button>
          </Upload>
        </div>
        <div>
          <Text>Change Summary (optional)</Text>
          <TextArea
            rows={3}
            placeholder="Describe what changed in this version..."
            value={changeSummary}
            onChange={(e) => setChangeSummary(e.target.value)}
            style={{ marginTop: '8px' }}
          />
        </div>
      </Modal>
    </Drawer>
  );
}

export default VersionHistoryDrawer;
